import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';

interface ProgramTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  program_type: string;
  is_featured?: boolean;
  settings: {
    points_per_currency?: number;
    points_for_reward?: number;
    stamps_required?: number;
    reward_type: string;
    reward_value?: number;
    reward_description: string;
    stamp_expiry_days?: number;
  };
}

interface TemplateSelectionDialogProps {
  open: boolean;
  onSelectTemplate: (template: ProgramTemplate) => void;
  onSkip: () => void;
  onClose: () => void;
}

const TemplateSelectionDialog: React.FC<TemplateSelectionDialogProps> = ({
  open,
  onSelectTemplate,
  onSkip,
  onClose
}) => {
  const [templates, setTemplates] = useState<ProgramTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('loyalty_program_templates')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('category');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...Array.from(new Set(templates.map(t => t.category)))];
  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Choose a Template to Get Started</DialogTitle>
          <DialogDescription>
            Select a pre-configured template to quickly set up your loyalty program, or start from scratch.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          {categories.map(category => (
            <Button
              key={category}
              size="sm"
              variant={selectedCategory === category ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(category)}
              className="capitalize"
            >
              {category === 'all' ? 'All Templates' : category}
            </Button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-gray-500">Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-gray-500">No templates found</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredTemplates.map((template) => (
                <Card 
                  key={template.id} 
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => onSelectTemplate(template)}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          {template.name}
                          {template.is_featured && (
                            <Sparkles className="h-4 w-4 text-yellow-500" />
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {template.description}
                        </CardDescription>
                      </div>
                      <Badge className="ml-2">
                        {template.category}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Type:</strong> {template.program_type}</p>
                      <p><strong>Reward:</strong> {template.settings.reward_description}</p>
                      {template.program_type === 'stamps' && (
                        <p><strong>Stamps Required:</strong> {template.settings.stamps_required}</p>
                      )}
                      {template.program_type === 'points' && (
                        <p><strong>Points for Reward:</strong> {template.settings.points_for_reward}</p>
                      )}
                    </div>
                    <Button 
                      className="w-full mt-4" 
                      variant="outline" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectTemplate(template);
                      }}
                    >
                      Use This Template
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-gray-500">
            Templates help you get started quickly with proven configurations
          </p>
          <Button 
            variant="ghost" 
            onClick={onSkip}
          >
            Skip and Start from Scratch
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelectionDialog;